import i18next from 'i18next';
import { useMutation } from 'react-query';
import { InvoiceInterface } from '../../../interfaces/invoice.interface';
import { GenericResponse } from '../../../interfaces/shared.interface';
import { getPostSalePreflightErrorCode } from '../../../services/utils/postSalePreflightUtils';
import { setToast } from '../../../store/core/coreSlice';
import { store } from '../../../store/store';
import { queryClient } from '../../APIProvider';
import { apiRQ } from '../../api';
import { getHttpMethod } from '../../helpers';

interface CreateDocResponse extends GenericResponse {
  entity: InvoiceInterface;
}

const updateCreateSale = async (variables: any): Promise<CreateDocResponse> => {
  variables.sendToAade = undefined;
  const isClone = variables.isClone;
  delete variables.isClone;
  if (!variables?.id || isClone) {
    variables.currency = 'EUR';
    variables.seriesName = variables.seriesName || '0';
  }

  const resp = await apiRQ[getHttpMethod(variables?.id, isClone)]<CreateDocResponse>('/invoices/', {
    data: variables,
  });

  return resp.data;
};

export const useCreateUpdateSale = () => {
  return useMutation<CreateDocResponse, Error>(updateCreateSale, {
    onSettled: async (_res, _error, variables: any) => {
      await queryClient?.invalidateQueries({ queryKey: ['getSale', variables?.id] });
      await queryClient?.invalidateQueries({ queryKey: ['getSales'] });
    },
  });
};

/**
 * PostSaleData
 * @param data
 */
const postSale = async (data: any): Promise<GenericResponse> => {
  const resp = await apiRQ.post<GenericResponse>('/invoices/post-my-data', data);
  await queryClient?.invalidateQueries({ queryKey: ['getSale', data?.id] });
  await queryClient?.invalidateQueries({ queryKey: ['useSaleState', data?.id] });

  return resp.data;
};

// Thrown from `onMutate` below to short-circuit the actual API call - react-query (v3) skips
// `mutationFn` entirely when `onMutate` rejects (confirmed directly against the installed
// `node_modules/react-query/lib/core/mutation.js`: it jumps straight to `onError`/`onSettled`),
// so this never reaches the network. Carries the `API_ERRORS` translation key so `onError` below
// can tell a pre-flight failure apart from a genuine request error.
class PostSalePreflightError extends Error {
  constructor(public readonly translationKey: string) {
    super(`Post sale pre-flight check failed: ${translationKey}`);
  }
}

export const usePostSale = (invoice: InvoiceInterface | undefined) => {
  return useMutation<GenericResponse, Error>(postSale, {
    onMutate: () => {
      // Catches the same staleness bugs myDATA would otherwise reject at transmission time (see
      // `postSalePreflightUtils.ts`), without a round trip.
      const translationKey = getPostSalePreflightErrorCode(invoice);
      if (translationKey) {
        throw new PostSalePreflightError(translationKey);
      }
    },
    onSuccess: async (data) => {
      if (data.success) {
        let duration = 3000;
        let copy = 'DOC_SUCCESSFULLY_POSTED';
        if (invoice?.post_with_provider) {
          copy = 'DOC_SUCCESSFULLY_SENT_TO_PROVIDER';
          duration = 10000;
        }
        store.dispatch(setToast({ value: { show: true, msg: i18next.t(`SALE.${copy}`), position: 'top', duration } }));
        return;
      }

      if (data.errorCode) {
        // Error is going to be shown from middleware
        return;
      }

      let errorMsg = i18next.t('API_ERRORS.SOMETHING_WENT_WRONG');
      if (typeof data?.error === 'string') errorMsg = data.error;

      if (errorMsg) {
        store.dispatch(
          setToast({
            value: { show: true, msg: errorMsg, isError: true },
          }),
        );
      }
    },
    onError: (error, variables: any) => {
      if (error instanceof PostSalePreflightError) {
        store.dispatch(
          setToast({
            value: { show: true, msg: i18next.t(`API_ERRORS.${error.translationKey}`), isError: true },
          }),
        );
        return;
      }

      queryClient?.invalidateQueries({ queryKey: ['getSale', variables?.id] });
    },
  });
};
